import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { throwIfNotOk } from "@/lib/api-error";
import { toast } from "@/lib/toast";
import { formatDateWithOptionsBs } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import {
  UserPlus,
  Trash,
  CircleNotch,
  Envelope,
  Clock,
  CheckCircle,
  MagnifyingGlass,
  Users,
} from "@phosphor-icons/react";
import type { Employee } from "@/types";

export const Route = createFileRoute("/dashboard/supervisor/employees")({
  component: EmployeesPage,
});

function EmployeesPage() {
  const { t } = useTranslation();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<Employee | null>(null);
  const [search, setSearch] = useState("");
  const queryClient = useQueryClient();

  const { data: employees, isLoading } = useQuery({
    queryKey: ["supervisor", "employees"],
    queryFn: async () => {
      const res = await fetch("/api/supervisor/employees");
      await throwIfNotOk(res, t("supervisor.employees.errorLoading"));
      return res.json() as Promise<Employee[]>;
    },
  });

  const removeMutation = useMutation({
    mutationFn: async (employeeId: string) => {
      const res = await fetch(`/api/supervisor/employees/${employeeId}`, {
        method: "DELETE",
      });
      await throwIfNotOk(res, t("supervisor.employees.errorRemoving"));
    },
    onSuccess: () => {
      toast.success(t("supervisor.employees.removeSuccess"));
      queryClient.invalidateQueries({ queryKey: ["supervisor", "employees"] });
      queryClient.invalidateQueries({ queryKey: ["supervisor", "company"] });
      setRemoveTarget(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const filtered = employees?.filter((emp) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      emp.name?.toLowerCase().includes(q) ||
      emp.inviteEmail.toLowerCase().includes(q) ||
      emp.email?.toLowerCase().includes(q)
    );
  });

  const accepted = filtered?.filter((e) => e.inviteStatus === "accepted") ?? [];
  const pending = filtered?.filter((e) => e.inviteStatus === "pending") ?? [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="overflow-hidden rounded-2xl border border-border/60 shadow-sm" style={{ background: "linear-gradient(135deg, oklch(28% 0.04 84), oklch(22% 0.03 80))" }}>
        <div className="px-6 py-8 sm:px-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-white" style={{ fontFamily: "var(--font-heading)" }}>
                {t("supervisor.employees.title")}
              </h1>
              <p className="mt-1 text-sm" style={{ color: "oklch(75% 0.02 84)" }}>
                {isLoading ? t("common.loading") : `${employees?.length ?? 0} ${t("supervisor.employees.total")} · ${accepted.length} ${t("supervisor.employees.activeCount")} · ${pending.length} ${t("supervisor.employees.pendingCount")}`}
              </p>
            </div>
            <Button
              onClick={() => setInviteOpen(true)}
              className="shrink-0 gap-2 bg-white/10 text-white backdrop-blur-sm hover:bg-white/20 border-white/10"
            >
              <UserPlus className="size-4" />
              {t("supervisor.employees.invite")}
            </Button>
          </div>

          {/* Search */}
          {(employees?.length ?? 0) > 0 && (
            <div className="relative mt-5">
              <MagnifyingGlass className="absolute left-3 top-1/2 size-4 -translate-y-1/2" style={{ color: "oklch(65% 0.02 84)" }} />
              <input
                type="text"
                placeholder={t("supervisor.employees.searchPlaceholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-xl border-0 py-2.5 pl-10 pr-4 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-white/20"
                style={{ background: "oklch(20% 0.02 84 / 0.6)" }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-28 animate-pulse rounded-xl border border-border/40 bg-card" />
          ))}
        </div>
      ) : !filtered?.length ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border/60 py-16">
          <div className="flex size-14 items-center justify-center rounded-full" style={{ background: "oklch(92% 0.04 84)" }}>
            <Users className="size-6" style={{ color: "oklch(55% 0.1 84)" }} />
          </div>
          <p className="mt-4 text-sm font-medium text-foreground">
            {search ? t("supervisor.employees.noResults") : t("supervisor.employees.noEmployees")}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {search ? t("supervisor.employees.tryDifferentTerm") : t("supervisor.employees.clickInvite")}
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          {/* Active employees */}
          {accepted.length > 0 && (
            <div>
              <div className="mb-3 flex items-center gap-2">
                <div className="flex size-5 items-center justify-center rounded-full" style={{ background: "oklch(88% 0.08 155)" }}>
                  <CheckCircle className="size-3" style={{ color: "oklch(50% 0.15 155)" }} />
                </div>
                <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  {t("supervisor.employees.active")} ({accepted.length})
                </span>
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {accepted.map((emp) => (
                  <EmployeeCard key={emp.id} employee={emp} onRemove={() => setRemoveTarget(emp)} />
                ))}
              </div>
            </div>
          )}

          {/* Pending employees */}
          {pending.length > 0 && (
            <div>
              <div className="mb-3 flex items-center gap-2">
                <div className="flex size-5 items-center justify-center rounded-full" style={{ background: "oklch(90% 0.06 84)" }}>
                  <Clock className="size-3" style={{ color: "oklch(55% 0.12 84)" }} />
                </div>
                <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  {t("supervisor.employees.pending")} ({pending.length})
                </span>
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {pending.map((emp) => (
                  <EmployeeCard key={emp.id} employee={emp} onRemove={() => setRemoveTarget(emp)} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Invite dialog */}
      {inviteOpen && <InviteDialog onClose={() => setInviteOpen(false)} />}

      {/* Remove confirmation */}
      {removeTarget && (
        <Dialog open onClose={() => setRemoveTarget(null)}>
          <DialogHeader onClose={() => setRemoveTarget(null)}>
            <DialogTitle>{t("supervisor.employees.removeTitle")}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-muted-foreground">
              {t("supervisor.employees.removeConfirm")}{" "}
              <strong className="text-foreground">{removeTarget.name || removeTarget.inviteEmail}</strong>?
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveTarget(null)}>{t("common.cancel")}</Button>
            <Button
              variant="destructive"
              onClick={() => removeMutation.mutate(removeTarget.id)}
              disabled={removeMutation.isPending}
            >
              {removeMutation.isPending && <CircleNotch className="size-4 animate-spin" />}
              {t("common.remove")}
            </Button>
          </DialogFooter>
        </Dialog>
      )}
    </div>
  );
}

/* ─── Employee Card ─── */

function EmployeeCard({ employee: emp, onRemove }: { employee: Employee; onRemove: () => void }) {
  const { t } = useTranslation();
  const isActive = emp.inviteStatus === "accepted";
  const initial = (emp.name || emp.inviteEmail).charAt(0).toUpperCase();

  return (
    <div className="group relative overflow-hidden rounded-xl border border-border/50 bg-card transition-all hover:border-border hover:shadow-sm">
      <div className="p-4">
        <div className="flex items-start gap-3">
          {/* Avatar */}
          <div
            className="flex size-10 shrink-0 items-center justify-center rounded-full text-sm font-bold"
            style={{
              background: isActive
                ? "linear-gradient(135deg, oklch(88% 0.06 84), oklch(82% 0.1 84))"
                : "oklch(94% 0.01 84)",
              color: isActive ? "oklch(45% 0.12 84)" : "oklch(60% 0.02 84)",
              fontFamily: "var(--font-heading)",
            }}
          >
            {initial}
          </div>

          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-foreground">
              {emp.name || emp.inviteEmail.split("@")[0]}
            </p>
            <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-muted-foreground">
              <Envelope className="size-3 shrink-0" />
              {emp.email || emp.inviteEmail}
            </p>
          </div>

          {/* Status dot */}
          <div className="flex items-center gap-1.5">
            <div
              className={cn("size-2 rounded-full", isActive ? "bg-emerald-500" : "bg-amber-400")}
            />
            <span className="text-[11px] font-medium text-muted-foreground">
              {isActive ? t("supervisor.employees.statusActive") : t("supervisor.employees.statusWaiting")}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-3 flex items-center justify-between border-t border-border/30 pt-3">
          <span className="text-[11px] text-muted-foreground">
            {emp.joinedAt
              ? `${t("supervisor.employees.joined")} ${formatDateWithOptionsBs(emp.joinedAt, { day: "numeric", month: "short", year: "numeric", timeZone: "Europe/Sarajevo" })}`
              : t("supervisor.employees.inviteSent")}
          </span>
          <button
            onClick={onRemove}
            className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-muted-foreground opacity-0 transition-all hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100"
          >
            <Trash className="size-3" />
            {t("common.remove")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Invite Dialog ─── */

function InviteDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const queryClient = useQueryClient();

  const inviteMutation = useMutation({
    mutationFn: async (inviteEmail: string) => {
      const res = await fetch("/api/supervisor/employees/invite", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: inviteEmail }),
      });
      await throwIfNotOk(res, t("supervisor.employees.errorInviting"));
      return res.json();
    },
    onSuccess: () => {
      toast.success(t("supervisor.employees.inviteSuccess"));
      queryClient.invalidateQueries({ queryKey: ["supervisor", "employees"] });
      queryClient.invalidateQueries({ queryKey: ["supervisor", "company"] });
      setEmail("");
      onClose();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <Dialog open onClose={onClose}>
      <DialogHeader onClose={onClose}>
        <DialogTitle>{t("supervisor.employees.inviteTitle")}</DialogTitle>
      </DialogHeader>
      <form onSubmit={(e) => { e.preventDefault(); if (email.trim()) inviteMutation.mutate(email.trim()); }}>
        <DialogBody>
          <div className="space-y-3">
            <div className="flex size-12 items-center justify-center rounded-full mx-auto" style={{ background: "oklch(92% 0.04 84)" }}>
              <UserPlus className="size-5" style={{ color: "oklch(55% 0.1 84)" }} />
            </div>
            <p className="text-center text-sm text-muted-foreground">
              {t("supervisor.employees.inviteDescription")}
            </p>
            <div>
              <Label htmlFor="invite-email" className="text-xs">{t("supervisor.employees.emailLabel")}</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="zaposlenik@firma.ba"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1"
                required
                autoFocus
              />
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button type="submit" disabled={inviteMutation.isPending || !email.trim()}>
            {inviteMutation.isPending ? <CircleNotch className="size-4 animate-spin" /> : <Envelope className="size-4" />}
            {t("supervisor.employees.sendInvite")}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
